/*
 placeholder "line" that exists between each interaction node in a scheduling matrix and standalone
 AND in between each interval in a scheduling matrix
*/

import { useEffect, useState } from "react";
import InteractionNode from "../models/interactions/InteractionNode";
import AddChildInteractionButton from "./interactions/AddChildInteractionButton";
import { useDrop, DropTargetMonitor } from "react-dnd";
import InteractionShell from "./interactions/InteractionShell";
import { InteractionNodeDragItem } from "../helpers/DragHelper";
import { useStoreDispatch, useStoreSelector } from "../store/Hooks";
import { interactionTreeSliceActions } from "../store/slices/InteractionTreeSlice";
import useHttp from "../hooks/useHttp";
import MatrixCell from "./communications/SchedulingMatrix/MatrixCell";
import InteractionIntervalSetting from "../models/communications/InteractionIntervalSetting";
import TableRow from "@mui/material/TableRow";
import MatrixCellControlsBox from "./communications/SchedulingMatrix/controls/MatrixCellControlsBox";
import MatrixInteractionControls from "./communications/SchedulingMatrix/controls/MatrixInteractionControls";
import Interaction from "../models/interactions/Interaction";
import Interval from "../models/communications/Interval";
import InteractionNodeInterval from "../models/interactions/InteractionNodeInterval";
import { stateDeepCopy } from "../helpers/DataUpdateHelper";

const testing = process.env.REACT_APP_TEST_MODE === "test";


interface InsertionPointProps {
  interactionNode: InteractionNode;
  interaction: Interaction;
  type: string;
  insertFunction?: any;
  associatedIntervalIDs?: Array<number>;
  intervalSetID: number | null;
  ordinal?: number;
  rootStyle?: any;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  intervalSettings?: Array<InteractionIntervalSetting> | null;
  accounts?: boolean;
}
{
  /* <InsertionPoint interactionNode={tempInteractionNode} type = {"intervalSet"} />  */
}

const InsertionPoint = (props: InsertionPointProps) => {
  const dispatch = useStoreDispatch();
  const { sendRequest: fetchData } = useHttp();

  const [rootStyle, setRootStyle] = useState<any>({ opacity: 1 });
  let style: any = {
    height: "3px" /* Height of the line */,
    width: "90%" /* Width of the line */,
    marginLeft: "auto",
    marginRight: "auto",
    backgroundColor: "lightskyblue" /* Line color */,
  };

  const handleOnMouseEnter = () => {
    // console.log(props.ordinal)
    // console.log(props.interactionNode)
    setRootStyle({
      marginTop: "-3000px",
      width: "6000px",
      zIndex: 11110,
      overflow: "visible",
    });
  };

  const handleOnMouseLeave = () => {
    setRootStyle({ opacity: 1 });
  };

  const rootInteractionID = useStoreSelector(
    (state) => state.interactionTree.RootInteractionID,
  );

  // drop listener logic
  const [{ isOver, item }, drop] = useDrop<
    InteractionNodeDragItem,
    void,
    { isOver: boolean | undefined; item: any }
  >({
    accept: "INTERACTION", // Specify the accepted draggable type
    drop: (item: InteractionNodeDragItem) => {
      // Handle the drop logic when a draggable is dropped here
      // console.log('Dropped item:', item);
      const thisInsertionPointOrdinal =
        typeof props.ordinal !== "undefined" ? props.ordinal : 1;

      // console.log(props.ordinal) // the dragged item node's ordinal on its current parent
      // console.log(thisInsertionPointOrdinal) // the dragged item node's ordinal on its current parent

      const newOrdinal =
        thisInsertionPointOrdinal === 0 ? 1 : thisInsertionPointOrdinal + 1;

      // console.log(props.intervalSetID) // if im dragging this to OUTSIDE of an interval set, this will be NULL
      // console.log(props.interactionNode) // this contains the parent of the destination
      // console.log(item.node.id) // the dragged item node id
      // console.log(item.node.parent)// the dragged item node's parent
      // console.log(item.node.ordinal) // the dragged item node's ordinal on its current parent
      // console.log(newOrdinal)  // the new ordinal of the item as it should be on the new interaction

      if (item.node.parent_interaction_id != props.interactionNode.parent_interaction_id) {
        // we probably need to handle the reordering of both the new parent AND the old parent
        // move/re-assign node to new parent, re-order parent in redux and in db
        handleUpdateNodeForNewParent(
          item.node,
          item.node.parent_interaction_id,
          props.interactionNode.parent_interaction_id,
          newOrdinal,
        );

        // reorder the old parent in db with node already re-assigned, and then update redux to fix old parent ordinal structure:
        fixOrdinalityByParent(
          item.node.id,
          item.node.parent_interaction_id,
          item.node.ordinal,
          item.node.ordinal, // delete the node at this ordinal
          true, // delete flag
        );
      } else {
        // THIS WORKS WELL
        let tempNewOrdinal = newOrdinal;
        if (item.node.ordinal < newOrdinal) {
          tempNewOrdinal = newOrdinal - 1;
        } else {
          tempNewOrdinal = newOrdinal;
        }
        handleDropReordering(
          item.node.id,
          item.node.parent_interaction_id,
          item.node.ordinal,
          tempNewOrdinal,
        );
      }

      // if moving interaction/survey from intervalset to outside of intervalset
      // delete interaction_node_intervals
      if (
        item.interactionIntervalSettings &&
        item.interactionIntervalSettings.length > 0 &&
        !props.intervalSetID
      ) {
        deleteInteractionNodeIntervalsViaIntervalSettings(
          item.interactionIntervalSettings,
        );
      }
    },
    collect: (monitor: DropTargetMonitor) => ({
      isOver: !!monitor.isOver(),
      item: monitor.getItem(),
    }),
  });

  const somethingInAppIsDragging = item !== null;

  useEffect(() => {
    if (somethingInAppIsDragging) {
      if (props.type == "intervalSet") {
        setRootStyle({
          opacity: 1,
          height: "3px",
          width: "100%",
          backgroundColor: "lightskyblue",
        });
      } else {
        setRootStyle({ opacity: 1 });
      }
    } else {
      if (props.type == "intervalSet") {
        setRootStyle({
          opacity: 0,
          height: "3px",
          width: "100%",
          backgroundColor: "lightskyblue",
        });
      } else {
        setRootStyle({ opacity: 1 });
      }
    }
  }, [somethingInAppIsDragging]);

  // update interactionNode containing this interaction on current parent
  // change the parent to the new parent
  // change the ordinal to the new ordinal
  // create new interactionNode
  const handleUpdateNodeForNewParent = async (
    node: InteractionNode,
    parentID: number,
    newParentID: number,
    ordinal: number,
  ) => {
    const data = stateDeepCopy(node);
    data.parent_interaction_id = newParentID;
    data.ordinal = ordinal;

    let thisInteractionNodeUrl = "";
    thisInteractionNodeUrl =
      process.env.REACT_APP_API_URL + "/interaction_node?id=eq." + node.id;
    const requestSettings = {
      url: thisInteractionNodeUrl,
      method: "PUT",
      body: data,
      headers: {
        Authorization: "Bearer " + process.env.REACT_APP_POSTGREST_JWT,
        "Content-Type": "application/json",
        "Content-Profile": testing ? "test" : "build"
      },
    };

    if (process.env.REACT_APP_DEV_MODE === "OFF") {
      // save outside entity with updated communication_set info
      await fetchData(requestSettings);
      await handleDropReordering(node.id, newParentID, ordinal, ordinal);
    }

    dispatch(
      interactionTreeSliceActions.updateInteractionNode({
        id: node.id,
        parent: newParentID,
        ordinal: ordinal,
      }),
    );

    dispatch(
      interactionTreeSliceActions.updateInteractionNodeOrdinalityByParentAndNode(
        {
          nodeid: node.id,
          parentid: newParentID,
          oldOrdinal: ordinal,
          newOrdinal: ordinal,
        },
      ),
    );
  };

  const handleDropReordering = async (
    nodeID: number,
    parentID: number,
    oldOrdinal: number,
    newOrdinal: number,
    deleteNode?: boolean,
  ) => {
    if (!deleteNode) {
      deleteNode = false;
    }

    const data = {
      nodeid: nodeID,
      parentid: parentID,
      old_ordinal: oldOrdinal,
      new_ordinal: newOrdinal,
    };
    const url =
      process.env.REACT_APP_API_URL + "/rpc/update_ordinality_by_parent";
    const requestSettings = {
      url: url,
      method: "POST",
      body: data,
      headers: {
        Authorization: "Bearer " + process.env.REACT_APP_POSTGREST_JWT,
        "Content-Type": "application/json",
        "Content-Profile": testing ? "test" : "build"
      },
    };
    if (process.env.REACT_APP_DEV_MODE === "OFF") {
      await fetchData(requestSettings);
    }
    dispatch(
      interactionTreeSliceActions.updateInteractionNodeOrdinalityByParentAndNode(
        {
          nodeid: nodeID,
          parentid: parentID,
          oldOrdinal: oldOrdinal,
          newOrdinal: newOrdinal,
          deleteNode: deleteNode,
        },
      ),
    );
  };

  const fixOrdinalityByParent = async (
    nodeID: number,
    parentID: number,
    oldOrdinal: number,
    newOrdinal: number,
    deleteNode?: boolean,
  ) => {
    const data = {
      parentid: parentID,
    };
    const url =
      process.env.REACT_APP_API_URL + "/rpc/fix_ordinality_for_parent";
    const requestSettings = {
      url: url,
      method: "POST",
      body: data,
      headers: {
        Authorization: "Bearer " + process.env.REACT_APP_POSTGREST_JWT,
        "Content-Type": "application/json",
        "Content-Profile": testing ? "test" : "build"
      },
    };
    if (process.env.REACT_APP_DEV_MODE === "OFF") {
      await fetchData(requestSettings);
    }
    dispatch(
      interactionTreeSliceActions.updateInteractionNodeOrdinalityByParentAndNode(
        {
          nodeid: nodeID,
          parentid: parentID,
          oldOrdinal: oldOrdinal,
          newOrdinal: newOrdinal,
          deleteNode: deleteNode,
        },
      ),
    );
  };

  // FIXME - REDUX STATE IS VERY CLOSE, BUT NOT QUITE THERE
  // WILL NEED TO PROBABLY RUN THESE DELETES AS A BATCH STATEMENT?
  const deleteInteractionNodeIntervalsViaIntervalSettings = async (
    intervalSettings: Array<InteractionIntervalSetting>,
  ) => {
    intervalSettings.forEach(async (intervalSetting) => {
      const url: string =
        process.env.REACT_APP_API_URL +
        "/interaction_node_interval?id=eq." +
        intervalSetting.interaction_node_interval_id;
      const requestSettings = {
        url: url,
        method: "DELETE",
        headers: {
          Authorization: "Bearer " + process.env.REACT_APP_POSTGREST_JWT,
          "Content-Type": "application/json",
          "Content-Profile": testing ? "test" : "build"
        },
      };
      if (process.env.REACT_APP_DEV_MODE === "OFF") {
        await fetchData(requestSettings);
      }
    });
    dispatch(
      interactionTreeSliceActions.deleteInteractionNodeIntervalsViaIntervalSettings(
        { intervalSettings: item.interactionIntervalSettings },
      ),
    );
  };

  const associatedIntervalIDsLength = props.associatedIntervalIDs
    ? props.associatedIntervalIDs.length
    : 0;
  const cell_width_percentage = 100 / (associatedIntervalIDsLength + 2);

  if (props.type == "intervalSet") {
    style = {
      height: "3px" /* Height of the line */,
      width: "100%" /* Width of the line */,
      // marginLeft: "auto",
      // marginRight: "auto",
      backgroundColor: "lightskyblue" /* Line color */,
    };
    // insertion point is always a table row
    // when not dragging/regular rendering:
    //  - tableRow child is just a standard on-hover insertion point
    // when dragging a row:
    //  - tablerow has matrixcell children representing a lightweight
    //    "preview" version of the real row
    //  - dont preview conditions or editor tool controls
    return (
      <TableRow
        ref={drop}
        onMouseEnter={props.onMouseEnter}
        onMouseLeave={props.onMouseLeave}
        style={{ backgroundColor: "#f0f2f2", height: "5px" }}
        // style={props.rootStyle}
      >
        {isOver && (
          <MatrixCell sx={{ width: cell_width_percentage * 2 + "%" }}>
            <MatrixInteractionControls
              condition_id={
                item.interactionNode ? item.interactionNode.condition_id : null
              }
              interaction={item.interaction}
              interaction_node={item.interactionNode}
              handleDeleteInteraction={null}
            />
          </MatrixCell>
        )}
        {isOver &&
          item.interactionIntervalSettings?.map(
            (interactionIntervalSetting: InteractionIntervalSetting) => (
              <MatrixCell
                sx={{ width: cell_width_percentage + "%" }}
                key={interactionIntervalSetting.interaction_node_interval_id}
              >
                {interactionIntervalSetting.selected}
                <MatrixCellControlsBox {...interactionIntervalSetting} />
              </MatrixCell>
            ),
          )}
        {!isOver && (
          <td
            style={{
              height: "0px",
              // padding:"0px",

              // marginBottom: "-35px",
              // // display: "flex",
              // // justifyContent: "center",
              // // alignItems: "center",
              position: "relative",
              // left: "45%",
              // zIndex: 5000,
            }}
          >
            {!somethingInAppIsDragging && (
              <AddChildInteractionButton
                setID={props.interactionNode.set_interaction_id}
                numChildren={1}
                parentID={props.interactionNode.parent_interaction_id}
                type={"insertIntervalSetInteraction"}
                main={false}
                ordinal={props.ordinal}
                handler={props.insertFunction}
                associatedIntervalIDs={props.associatedIntervalIDs}
                intervalSetID={props.intervalSetID}
                accounts={props.accounts}
              />
            )}
          </td>
        )}
        {/* <div style={style}></div> */}
        {/* "rgb(25, 118, 210)" */}
        {props.associatedIntervalIDs?.map((associatedIntervalID: number) => (
          <td style={{ border: "none !important" }} key={associatedIntervalID}>
            {" "}
          </td>
        ))}
      </TableRow>
    );
  } else {
    return (
      <span
        onMouseEnter={handleOnMouseEnter}
        onMouseLeave={handleOnMouseLeave}
        style={rootStyle}
        ref={drop}
      >
        {isOver && (
          <InteractionShell
            interactionID={item.interaction.id}
            rootInteractionID={rootInteractionID}
            interactionNodeID={item.node.id}
            dragPreview={true}
          />
        )}
        {!isOver && (
          <span
            style={{
              marginBottom: "-35px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "relative",
            }}
          >
            {/* Dont show circluar insert button, only show placeholder line */}
            {!somethingInAppIsDragging && (
              <AddChildInteractionButton
                setID={props.interactionNode.set_interaction_id}
                numChildren={1}
                parentID={props.interactionNode.parent_interaction_id}
                type={"insert"}
                main={false}
                ordinal={props.ordinal}
                intervalSetID={props.intervalSetID}
                accounts={props.accounts}
              />
            )}
            {/* adding buffer to take the place of button */}
            {somethingInAppIsDragging && (
              <div>
                <br />
                <br />
              </div>
            )}
          </span>
        )}
        <div style={style}></div>
      </span>
    );
  }
};

export default InsertionPoint;
